/**
 * b2b-cart-drawer.js
 *
 * Patches Cart.prototype.tryToUpdateQuantity with stable cart item keys.
 *
 * The base Blockshop cart uses positional line numbers for /cart/change.js.
 * Those indexes can go stale while the drawer is re-rendering, so this patch
 * resolves Shopify's stable line item key from the DOM and serializes changes.
 *
 * Must be loaded as type="module" after theme.js so that
 * customElements.get('cart-element') resolves to the Cart class.
 */
(function () {
  const CartClass = customElements.get('cart-element');
  if (!CartClass) return;

  const DEBOUNCE_MS = 300;
  const CART_UPDATING_ATTR = 'data-cart-updating';
  const CONTROL_LOCK_ATTR = 'data-b2b-cart-lock-disabled';
  const CART_LINE_CONTROL_SELECTOR = [
    '.cart--item--quantity .quantity-selector--minus',
    '.cart--item--quantity .quantity-selector--input',
    '.cart--item--quantity .quantity-selector--plus',
    '.cart--item--quantity .quantity-selector--remove',
  ].join(',');
  const _states = new WeakMap();

  function getState(cart) {
    let state = _states.get(cart);
    if (!state) {
      state = {
        timers: Object.create(null),
        pending: new Map(),
        processing: false,
        resyncing: null,
        cartKeys: null,
      };
      _states.set(cart, state);
    }
    return state;
  }

  function getSections() {
    return Array.isArray(CartClass.sections) ? CartClass.sections : ['cart-drawer'];
  }

  function getCartElements() {
    if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') return [];
    return [...document.querySelectorAll('cart-element')];
  }

  function isCartUpdateLocked() {
    return getCartElements().some((cart) => cart.getAttribute?.(CART_UPDATING_ATTR) === 'true');
  }

  function setCartLineControlsDisabled(cart, disabled) {
    if (!cart || typeof cart.querySelectorAll !== 'function') return;

    if (disabled) {
      cart.setAttribute?.(CART_UPDATING_ATTR, 'true');
    } else {
      cart.removeAttribute?.(CART_UPDATING_ATTR);
    }

    cart.querySelectorAll(CART_LINE_CONTROL_SELECTOR).forEach((control) => {
      if (disabled) {
        if (!control.hasAttribute?.(CONTROL_LOCK_ATTR)) {
          control.setAttribute?.(CONTROL_LOCK_ATTR, control.disabled ? 'true' : 'false');
        }

        control.disabled = true;
        control.setAttribute?.('aria-disabled', 'true');

        if (control.matches?.('.quantity-selector--input')) {
          control.setAttribute?.('aria-busy', 'true');
        }

        return;
      }

      if (control.hasAttribute?.(CONTROL_LOCK_ATTR)) {
        control.disabled = control.getAttribute(CONTROL_LOCK_ATTR) === 'true';
        control.removeAttribute?.(CONTROL_LOCK_ATTR);
      }

      control.removeAttribute?.('aria-disabled');

      if (control.matches?.('.quantity-selector--input')) {
        control.removeAttribute?.('aria-busy');
      }
    });
  }

  function setAllCartLineControlsDisabled(disabled) {
    getCartElements().forEach((cart) => setCartLineControlsDisabled(cart, disabled));
  }

  function findItemByLine(cart, lineNum) {
    if (lineNum === undefined || lineNum === null) return null;
    return cart.querySelector(`.cart--item[data-line-num='${lineNum}']`);
  }

  function findItemByKey(cart, itemKey) {
    if (!itemKey || typeof cart.querySelectorAll !== 'function') return null;
    return [...cart.querySelectorAll('.cart--item')].find((item) => getItemKey(item) === itemKey) || null;
  }

  function findCartItems(cart) {
    if (typeof cart.querySelectorAll !== 'function') return [];
    return [...cart.querySelectorAll('.cart--item')];
  }

  function getItemKey(cartItem) {
    return cartItem?.dataset?.cartItemKey || cartItem?.getAttribute?.('data-cart-item-key') || '';
  }

  function getVariantId(cartItem, itemKey = '') {
    return cartItem?.getAttribute?.('data-variant-id') || String(itemKey).split(':')[0] || '';
  }

  function getItemFingerprint(cartItem) {
    return cartItem?.dataset?.cartItemFingerprint || cartItem?.getAttribute?.('data-cart-item-fingerprint') || '';
  }

  function normalizeQuantity(quantity) {
    const parsed = parseInt(quantity, 10);
    return Number.isNaN(parsed) ? null : Math.max(0, parsed);
  }

  function getAdjustedQuantity(cart, cartItem, requestedQuantity) {
    const dataset = cartItem?.dataset || {};
    const inventoryQuantity = parseInt(dataset.quantity, 10);
    const variantId = cartItem?.getAttribute?.('data-variant-id');
    const otherQuantity = typeof cart.getOtherVariantQuantities === 'function'
      ? cart.getOtherVariantQuantities(cartItem, variantId)
      : 0;

    const inventoryLimited = (
      dataset.management === 'shopify' &&
      dataset.policy !== 'continue' &&
      !Number.isNaN(inventoryQuantity) &&
      inventoryQuantity < requestedQuantity + otherQuantity
    );

    return {
      quantity: inventoryLimited ? Math.max(0, inventoryQuantity - otherQuantity) : requestedQuantity,
      inventoryLimited,
    };
  }

  function syncCartFromResponse(cart, cartData) {
    const state = getState(cart);

    if (Array.isArray(cartData?.items)) {
      state.cartKeys = new Set(cartData.items.map((item) => item.key).filter(Boolean));
      CartClass.fillBasket(cartData.items);
    }

    if (Number.isInteger(cartData?.item_count)) {
      CartClass.updateTotals(cartData.item_count);
    }

    if (cartData?.sections) {
      CartClass.updateAllHtml(cartData.sections);

      if (isCartUpdateLocked()) {
        setAllCartLineControlsDisabled(true);
      }
    }
  }

  function showCartError(cart, change, message) {
    const cartItem = findItemForChange(cart, change);
    if (!cartItem) return;

    const errorEl = cartItem.querySelector('.cart--error');
    if (errorEl) {
      if (message) errorEl.textContent = message;
      errorEl.setAttribute('aria-hidden', 'false');
    }

    const quantityInput = cartItem.querySelector('.quantity-selector--input');
    const minMatch = message?.match(/minimum of (\d+)/i);
    if (quantityInput && minMatch) {
      quantityInput.value = parseInt(minMatch[1], 10);
    }
  }

  async function fetchCartSections(cart) {
    const sections = getSections();
    if (!sections.length) return;

    const path = window.location?.pathname || '/';
    const separator = path.includes('?') ? '&' : '?';
    const response = await fetch(`${path}${separator}sections=${encodeURIComponent(sections.join(','))}`);

    if (response.ok) {
      CartClass.updateAllHtml(await response.json());

      if (isCartUpdateLocked()) {
        setAllCartLineControlsDisabled(true);
      }
    }
  }

  async function resyncCart(cart) {
    const state = getState(cart);
    if (state.resyncing) return state.resyncing;

    state.resyncing = (async () => {
      try {
        const cartResponse = await fetch(`${Shopify.routes.cart}.js`, {
          headers: { Accept: 'application/json' },
        });

        if (cartResponse.ok) {
          syncCartFromResponse(cart, await cartResponse.json());
        }

        await fetchCartSections(cart);
      } catch {
        // Leave the current DOM in place if the resync itself fails.
      }
    })();

    try {
      await state.resyncing;
    } finally {
      state.resyncing = null;
    }
  }

  function isSameVariant(cartItem, variantId) {
    return !variantId || getVariantId(cartItem) === String(variantId);
  }

  function findUniqueItemByFingerprint(cart, fingerprint) {
    if (!fingerprint) return null;
    const matchingItems = findCartItems(cart).filter((cartItem) => (
      getItemKey(cartItem) &&
      getItemFingerprint(cartItem) === fingerprint
    ));

    return matchingItems.length === 1 ? matchingItems[0] : null;
  }

  function findItemForChange(cart, change) {
    return (
      findItemByKey(cart, change.itemKey) ||
      findUniqueItemByFingerprint(cart, change.fingerprint) ||
      (!change.fingerprint ? findItemByLine(cart, change.lineNum) : null)
    );
  }

  function findRemappedItem(cart, change) {
    const matchingItems = findCartItems(cart).filter((cartItem) => (
      getItemKey(cartItem) &&
      getItemKey(cartItem) !== change.itemKey &&
      (
        change.fingerprint
          ? getItemFingerprint(cartItem) === change.fingerprint
          : isSameVariant(cartItem, change.variantId)
      )
    ));

    return matchingItems.length === 1 ? matchingItems[0] : null;
  }

  async function resyncAndResolveChange(cart, change) {
    await resyncCart(cart);

    if (change.resynced) return null;

    const state = getState(cart);
    const currentItem = findItemByKey(cart, change.itemKey);

    if (currentItem && (!state.cartKeys || state.cartKeys.has(change.itemKey))) {
      return {
        ...change,
        variantId: getVariantId(currentItem, change.itemKey),
        fingerprint: getItemFingerprint(currentItem) || change.fingerprint,
        remapped: false,
        resynced: true,
      };
    }

    const cartItem = findRemappedItem(cart, change);
    const itemKey = getItemKey(cartItem);

    if (!itemKey || (state.cartKeys && !state.cartKeys.has(itemKey))) return null;

    return {
      ...change,
      itemKey,
      variantId: getVariantId(cartItem, itemKey),
      fingerprint: getItemFingerprint(cartItem) || change.fingerprint,
      remapped: itemKey !== change.itemKey,
      resynced: true,
    };
  }

  async function readErrorMessage(response) {
    try {
      const data = await response.clone().json();
      return data?.description || data?.message || response.statusText;
    } catch {
      return response.statusText;
    }
  }

  function isIdentifierCartError(status, message) {
    const normalizedMessage = String(message || '').toLowerCase();

    return (
      status === 400 ||
      normalizedMessage.includes('cannot find variant') ||
      normalizedMessage.includes('line parameter is invalid') ||
      normalizedMessage.includes('required parameter missing or invalid')
    );
  }

  async function submitChange(cart, change) {
    const state = getState(cart);

    if (state.cartKeys && !state.cartKeys.has(change.itemKey)) {
      const resolvedChange = await resyncAndResolveChange(cart, change);
      return resolvedChange ? submitChange(cart, resolvedChange) : true;
    }

    const cartItem = findItemForChange(cart, change);
    if (!cartItem || getItemKey(cartItem) !== change.itemKey) {
      const resolvedChange = await resyncAndResolveChange(cart, change);
      return resolvedChange ? submitChange(cart, resolvedChange) : true;
    }

    const { quantity, inventoryLimited } = getAdjustedQuantity(cart, cartItem, change.quantity);
    const response = await fetch(`${Shopify.routes.cart_change}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: change.itemKey,
        quantity,
        sections: getSections(),
      }),
    });

    if (response.ok) {
      syncCartFromResponse(cart, await response.json());
      return !inventoryLimited;
    }

    const message = await readErrorMessage(response);

    if (response.status === 400 || response.status === 422) {
      if (isIdentifierCartError(response.status, message)) {
        const resolvedChange = await resyncAndResolveChange(cart, change);
        if (resolvedChange?.remapped) return submitChange(cart, resolvedChange);
      }

      showCartError(cart, change, message);
      return true;
    }

    throw new Error(response.statusText);
  }

  async function processQueue(cart) {
    const state = getState(cart);
    if (state.processing) return;

    state.processing = true;
    setAllCartLineControlsDisabled(true);

    try {
      while (state.pending.size) {
        setAllCartLineControlsDisabled(true);

        const [itemKey, change] = state.pending.entries().next().value;
        state.pending.delete(itemKey);

        try {
          const successful = await submitChange(cart, change);
          if (!successful && change.quantity !== 0) {
            showCartError(cart, change);
          }
        } catch {
          await resyncCart(cart);
        }
      }
    } finally {
      state.processing = false;

      if (state.pending.size) {
        processQueue(cart);
      } else {
        setAllCartLineControlsDisabled(false);
        cart.toggleLoadingDisplay(true);
      }
    }
  }

  CartClass.prototype.tryToUpdateQuantity = function (lineNum, quantity) {
    const parsedQuantity = normalizeQuantity(quantity);
    if (parsedQuantity === null) return;

    const state = getState(this);
    const cartItem = findItemByLine(this, lineNum);
    const itemKey = getItemKey(cartItem);
    const variantId = getVariantId(cartItem, itemKey);

    this.toggleLoadingDisplay(false, lineNum);

    if (!itemKey) {
      setAllCartLineControlsDisabled(true);
      resyncCart(this).finally(() => {
        setAllCartLineControlsDisabled(false);
        this.toggleLoadingDisplay(true);
      });
      return;
    }

    clearTimeout(state.timers[itemKey]);
    state.timers[itemKey] = setTimeout(() => {
      delete state.timers[itemKey];
      state.pending.set(itemKey, {
        itemKey,
        lineNum,
        quantity: parsedQuantity,
        variantId,
        fingerprint: getItemFingerprint(cartItem),
      });
      processQueue(this);
    }, DEBOUNCE_MS);
  };
})();
